import * as L from 'leaflet';
import * as turf from '@turf/turf';
import { shoot } from './helpers';
import tippy from 'tippy.js';

const GLOBAL_MAX_DIST = 500;

export function getMarkerWithColor(color) {
  return new L.Icon({
    iconUrl:
      'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-' + color + '.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
}

export function getEarthquakeMarker() {
  return new L.Icon({
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    tooltipAnchor: [0, 0],
    iconRetinaUrl: '/img/marker-epicenter.png',
    iconUrl: '/img/marker-epicenter.png',
    shadowUrl: ''
  });
}

export function getStationMarker(disabled) {
  return new L.Icon({
    iconSize: [40, 40],
    iconAnchor: [20, 34],
    tooltipAnchor: [0, -5],
    iconRetinaUrl: disabled ? '/img/marker-default-disabled.png' : '/img/marker-default.png',
    iconUrl: disabled ? '/img/marker-default-disabled.png' : '/img/marker-default.png',
    shadowUrl: ''
  });
}

export function getTargetMarker(disabled) {
  return new L.Icon({
    iconSize: [40, 40],
    iconAnchor: [20, 34],
    tooltipAnchor: [0, 0],
    iconRetinaUrl: disabled ? '/img/marker-poi-disabled.png' : '/img/marker-poi.png',
    iconUrl: disabled ? '/img/marker-poi-disabled.png' : '/img/marker-poi.png',
    shadowUrl: ''
  });
}

export function getTrianglePolygon(stationLoc, earthquakeLoc) {
  const point1 = turf.point(stationLoc);
  const point2 = turf.point(earthquakeLoc);
  const bearing = turf.bearing(point1, point2);
  const maxdist = GLOBAL_MAX_DIST;
  const point3 = shoot(stationLoc[0], stationLoc[1], turf.bearingToAzimuth(bearing + 5), maxdist);
  const point4 = shoot(stationLoc[0], stationLoc[1], turf.bearingToAzimuth(bearing - 5), maxdist);
  const polygonPoints = [[stationLoc, point3, point4]];

  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: polygonPoints
    }
  };
}

export function getRoundedValue(value) {
  return parseFloat(value) ? `${Math.round(100 * value) / 100}s` : value;
}

export function getHeadData(headData) {
  return headData.map((value) => `<th>${value}</th>`).join('');
}

export function getBody(tableData) {
  return tableData
    .map((row, index) =>
      index === 0
        ? `<tr>${renderRow(
            row
          )}</tr><tr><td></td><td colspan="2" class="text--center ignore-bg">Warning Time(s) <i class="tooltip__anchor" id="warning-times-tooltip">?</i></td></tr>`
        : `<tr>${renderRow(row)}</tr>`
    )
    .join('');
}

export function renderRow(row) {
  return row.map((value) => `<td>${getRoundedValue(value)}</td>`).join('');
}

export function getLegendItems() {
  return [
    renderLegendItem('legendImg3', '/img/marker-epicenter.png', 'Earthquake', 'addEarthquakeButton', 'Set'),
    renderLegendItem('legendImg', '/img/marker-poi.png', 'Target Site', 'addTargetButton', 'Add new'),
    renderLegendItem('legendImg', '/img/marker-default.png', 'Station', 'addStationButton', 'Add new'),
    renderResetButton()
  ];
}

export function renderLegendItem(className, image, text, buttonId, buttonLabel) {
  let element = `<p class="legendP"><img alt="" class="${className}" src="${image}">${text}`;
  if (buttonId) {
    element = `${element} <button type="button" id="${buttonId}" class="legendButton">${buttonLabel}</button>`;
  }

  return `${element} </p>`;
}

export function renderResetButton() {
  return `<p class="legendP"><button type="button" id="resetMapButton" class="button button--tertiary button--sm button--block">Reset</button></p>`;
}

export function initTableTooltips() {
  tippy('#other-systems-tooltip', {
    allowHTML: true,
    content: `
      <p>"Other systems" is a generic reference of systems that are based on regional source-based seismic algorithms. The estimations are not accurate and meant to provide a reference point. The following are the base assumptions for the algorithm used by these systems:</p>
      <ul>
        <li>A minimum of 4 stations is required to locate the earthquake epicenter</li>
        <li>Location time is based on P wave arrivals only</li>
      </ul>`
  });

  tippy('#time-to-alert-tooltip', {
    allowHTML: true,
    content: `
      <p>The time in seconds from the rupture of the earthquake to the time the system accurately determines the location, magnitude and estimated ground shaking at the target sites. </p>
      <p>The shorter the time, the earlier an alert can be issued to the target sites.</p>`
  });

  tippy('#warning-times-tooltip', {
    allowHTML: true,
    content: `
      <p>The time in seconds each target site will have from the time the alert was received and the arrival of the destructive seismic waves.</p>
      <p>The longer the time, the more time will the sites have to take preventive measures to reduce the damages from the coming shaking</p>`
  });
}

export function generateTable(tableData) {
  const table = document.createElement('table');
  table.className = 'table';
  const head = document.createElement('thead');
  const body = document.createElement('tbody');

  const headData = tableData[0];
  tableData.splice(0, 1);

  head.innerHTML = `<tr>${getHeadData(headData)}</tr>`;
  body.innerHTML = getBody(tableData);

  table.appendChild(head);
  table.appendChild(body);

  document.getElementById('sidebar-content').innerHTML = '';
  document.getElementById('sidebar-content').appendChild(table);
  setTimeout(() => {
    table.classList.add('flash');
  }, 100);

  initTableTooltips();
}

export function makeTableHTML(myArray) {
  let result = '<table border="1" style="text-align: center; vertical-align: middle;">';
  result +=
    '<tr><td colspan="2"></td><td colspan="' + (myArray[0].length - 2).toString() + '">Warning time (s)</td></tr>';
  for (let i = 0; i < myArray.length; i++) {
    result += '<tr>';
    for (let j = 0; j < myArray[i].length; j++) {
      let value = myArray[i][j];
      if (parseFloat(value)) {
        value = (Math.round(100 * value) / 100).toString();
      }
      result += '<td>' + value + '</td>';
    }
    result += '</tr>';
  }
  result += '</table>';

  return result;
}

export function toggleSidebar(enable) {
  const sidebar = document.getElementById('sidebar');
  if (enable) {
    sidebar.classList.add('sidebar--shown');
  } else {
    sidebar.classList.remove('sidebar--shown');
  }
}

export function toggleSaveUpload(enable) {
  const sidebarFooter = document.getElementById('sidebar__footer');
  if (enable) {
    sidebarFooter.classList.add('sidebar__footer--shown');
  } else {
    sidebarFooter.classList.remove('sidebar__footer--shown');
  }
}

export function loadEarthquakeData(map, backgroundMinMag) {
  const bounds = map.getBounds();
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const params = [
    { name: 'format', value: 'geojson' },
    { name: 'starttime', value: '1900-01-01' },
    { name: 'endtime', value: '2030-01-02' },
    { name: 'minmagnitude', value: `${backgroundMinMag}` },
    { name: 'minlatitude', value: sw.lat.toString() },
    { name: 'maxlatitude', value: ne.lat.toString() },
    { name: 'minlongitude', value: sw.lng.toString() },
    { name: 'maxlongitude', value: ne.lng.toString() }
  ];

  const url = `https://earthquake.usgs.gov/fdsnws/event/1/query?${params
    .map((param) => `${param.name}=${param.value}`)
    .join('&')}`;

  return fetch(url).then((res) => res.json());
}
